import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH ALL PACKAGES
export const fetchAllPackages = createAsyncThunk(
  "packages/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/packages", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch packages");
    }
  }
);

// FETCH PACKAGE STATS (summary cards)
export const fetchPackageStats = createAsyncThunk(
  "packages/fetchStats",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/packages/stats", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch package stats");
    }
  }
);

// FETCH PACKAGE LIST
export const fetchPackageList = createAsyncThunk(
  "packages/getPackageList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/packages/package-list", {
        params,
      });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch package list");
    }
  }
);

// CREATE PACKAGE
export const addPackage = createAsyncThunk(
  "packages/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/packages", payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

// FETCH SINGLE PACKAGE
export const getPackageById = createAsyncThunk(
  "packages/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/packages/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch package");
    }
  }
);

// UPDATE PACKAGE
export const updatePackage = createAsyncThunk(
  "packages/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/packages/${id}`, payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

// DELETE PACKAGE BY ID
export const deletePackage = createAsyncThunk(
  "packages/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/packages/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete package");
    }
  }
);

// CHANGE PACKAGE STATUS
export const changePackageStatus = createAsyncThunk(
  "packages/changeStatus",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/packages/${id}/change-status`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to change status");
    }
  }
);

// BULK DELETE PACKAGES
export const bulkDeletePackages = createAsyncThunk(
  "packages/bulkDelete",
  async (packageIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/packages/bulk-delete`, {
        data: { ids: packageIds },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NOTE : COMMENTED CODE FOR OLD PDF PREVIEW SLOW AND BELOW CODE FOR FAST

// PREVIEW PACKAGE PDF
// export const previewPackagePdf = createAsyncThunk(
//   "packages/previewPdf",
//   async (payload, { rejectWithValue }) => {
//     try {
//       const response = await Axios.post("/tenant/packages/preview", payload);
//       if (response.data?.success === false) {
//         return rejectWithValue(response.data);
//       }
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(
//         error?.response?.data || error.message || "Failed to generate preview",
//       );
//     }
//   },
// );

export const previewPackagePdf = createAsyncThunk(
  "packages/previewPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/fast-package-preview", payload, {
        responseType: "blob",
      });

      let blob = response.data;
      let pdf_url = null;
      let pdf_base64 = null;

      // Smart Handling: JSON or PDF
      if (blob.type === "application/json") {
        const text = await blob.text();
        const json = JSON.parse(text);
        pdf_url = json.data?.pdf_url || json.pdf_url;
        pdf_base64 = json.data?.pdf_base64 || json.pdf_base64;

        if (pdf_base64) {
          const byteCharacters = atob(pdf_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([new Uint8Array(byteNumbers)], {
            type: "application/pdf",
          });
        } else if (pdf_url) {
          const pdfResponse = await fetch(pdf_url);
          blob = await pdfResponse.blob();
        }
      }

      if (blob.type !== "application/pdf") {
        throw new Error("Failed to load PDF content");
      }

      return { blob, pdf_url, pdf_base64 }; // Standardized return
    } catch (error) {
      return rejectWithValue(
        error?.response?.data || error.message || "Failed to generate preview"
      );
    }
  }
);

const packagesSlice = createSlice({
  name: "packages",
  initialState: {
    packages: [],
    packagesList: [],
    total_records: 0,
    isLoading: false,
    error: null,
    isBulkDeleting: false,
    stats: null,
    isStatsLoading: false,
  },

  extraReducers: (builder) => {
    builder
      // PACKAGE STATS
      .addCase(fetchPackageStats.pending, (state) => {
        state.isStatsLoading = true;
      })
      .addCase(fetchPackageStats.fulfilled, (state, action) => {
        state.isStatsLoading = false;
        state.stats = action.payload || state.stats;
      })
      .addCase(fetchPackageStats.rejected, (state) => {
        state.isStatsLoading = false;
      })

      // FETCH ALL PACKAGES
      .addCase(fetchAllPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllPackages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packages = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch packages";
      })

      //FETCH PACKAGE LIST
      .addCase(fetchPackageList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPackageList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packagesList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchPackageList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch package list";
      });
  },
});

export default packagesSlice.reducer;
